import { useEffect, useState } from 'react'
import {
  IndexVixellPalletsResponseDataComponent,
  ShippingStateValue,
} from '../../types/typescript-axios'

import { DefaultWithTitleLayout } from '../../components/templates/default-with-title-layout'
import { VixellTable } from '../../components/part/vixell-table'
import { TableElementDatetime2Line } from '../../components/part/table-elements/table-element-datetime-2-line'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AdminVixellsSearchModal } from '../../components/templates/admin-vixells/admin-vixells-search-modal'
import moment from 'moment'
import { Lang } from '../../translations/lang'
import { webRequest } from '../../types/handle-api'
import { usePaging } from '../../contexts/use-paging'
import { VixellPalletVixellList } from '../../components/templates/admin-vixell-pallets/admin-vixell-pallets-vixell-list'
import { SearchInput } from '../../components/molecules/search-input'
import { Select } from '../../components/molecules/select'
import { ShippingStateSelects } from './common/shipping-state-selects'
import { toFormattedDatetime } from '../../utils/to-formatted-datetime'
import { downloadCsv } from '../../utils/download-csv'

export const AdminVixellPallets = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [pallets, setPallets] = useState<
    IndexVixellPalletsResponseDataComponent[]
  >([])

  const [searchWord, setSearchWord] = useState<string>('')
  const [searchShippingState, setSearchShippingState] = useState<string>('')
  const [from, setFrom] = useState<string>('')
  const [till, setTill] = useState<string>('')

  const [showSearchModal, setShowSearchModal] = useState<boolean>(false)

  const [loadNextPageAt, setLoadNextPageAt] = useState<Date>(new Date())
  const [searchParamsString, setSearchParamsString] = useState<string>()
  const { currentPage, setPaging, stopPageLoad } = usePaging()

  useEffect(() => {
    fetchVixellPalletsAndSetStates()
  }, [searchParams])

  async function fetchVixellPalletsAndSetStates(loadNextPage = false) {
    const response = await fetchVixellPallets({ loadNextPage })
    if (response.status === 200) {
      const data = response.data
      if (data.type === 'data') {
        if (searchParamsString === searchParams.toString()) {
          setPallets((v) => [...v, ...data.pallets])
        } else {
          setPallets(data.pallets)
        }
        setPaging(data)
        setSearchParamsString(searchParams.toString())
      }
    }
  }

  async function fetchVixellPallets({
    all = false,
    loadNextPage = false,
  } = {}) {
    const query = searchParams.get('query')
    const shippingState = searchParams.get('shippingState')
    const latestShippingStateDateTimeFrom = searchParams.get('from')
    const latestShippingStateDateTimeTill = searchParams.get('till')

    setSearchWord((w) => w || query || '')
    setSearchShippingState(shippingState || '')
    setFrom(latestShippingStateDateTimeFrom || '')
    setTill(latestShippingStateDateTimeTill || '')

    return await webRequest('VixellPalletsIndex', {
      ...(query && { query }),
      ...(shippingState && {
        shippingState: shippingState as ShippingStateValue,
      }),
      ...(latestShippingStateDateTimeFrom && {
        latestBelongingLogDateTimeFrom: latestShippingStateDateTimeFrom,
      }),
      ...(latestShippingStateDateTimeTill && {
        latestBelongingLogDateTimeTill: latestShippingStateDateTimeTill,
      }),
      ...(all && { all: true }),
      ...(!all && loadNextPage && { page: currentPage + 1 }),
    })
  }

  async function csvDownload() {
    console.log('csvDownload')
    const response = await fetchVixellPallets({ all: true })
    if (response.status === 200) {
      const csvData = [
        [
          Lang.vixellPalletSerialNo,
          Lang.model,
          Lang.siteName,
          `${Lang.admin.vixellPallets.composedVicSerial} 1`,
          `${Lang.admin.vixellPallets.composedVicSerial} 2`,
          `${Lang.admin.vixellPallets.composedVicSerial} 3`,
          `${Lang.admin.vixellPallets.composedVicSerial} 4`,
          `${Lang.admin.vixellPallets.composedVicSerial} 5`,
          `${Lang.admin.vixellPallets.composedVicSerial} 6`,
          Lang.shippingState,
          Lang.updatedDateTime,
        ],
        ...response.data.pallets.map(
          ({
            serialNo,
            model,
            siteName,
            vixells,
            shippingState,
            belongingLogProcessingDateTime,
          }) => [
            serialNo,
            model,
            siteName,
            vixells.length > 0 ? vixells[0].serialNo : '',
            vixells.length > 1 ? vixells[1].serialNo : '',
            vixells.length > 2 ? vixells[2].serialNo : '',
            vixells.length > 3 ? vixells[3].serialNo : '',
            vixells.length > 4 ? vixells[4].serialNo : '',
            vixells.length > 5 ? vixells[5].serialNo : '',
            shippingState && Lang.shippingStateName[shippingState],
            toFormattedDatetime(belongingLogProcessingDateTime),
          ]
        ),
      ]
      downloadCsv(csvData, `VIXELLPallets-${new Date().toISOString()}.csv`)
    }
  }

  function infiniteLoad() {
    if (stopPageLoad) return
    if (moment(loadNextPageAt).isAfter(moment().subtract(3, 'seconds'))) return

    setLoadNextPageAt(new Date())
    fetchVixellPalletsAndSetStates(true)
  }

  function search() {
    if (searchWord) searchParams.set('query', searchWord)
    if (!searchWord) searchParams.delete('query')
    navigate(`/vixell-pallets?${searchParams.toString()}`)
  }

  function changeFrom(value: string) {
    if (value) searchParams.set('from', value)
    if (!value) searchParams.delete('from')
    navigate(`/vixell-pallets?${searchParams.toString()}`)
  }

  function changeTill(value: string) {
    if (value) searchParams.set('till', value)
    if (!value) searchParams.delete('till')
    navigate(`/vixell-pallets?${searchParams.toString()}`)
  }

  function selectShippingState(value: string) {
    if (value) searchParams.set('shippingState', value)
    if (!value) searchParams.delete('shippingState')
    navigate(`/vixell-pallets?${searchParams.toString()}`)
  }

  const titleRightElement = (
    <div className="flex flex-col items-end">
      <div>
        <SearchInput
          value={searchWord}
          onChange={setSearchWord}
          onClick={search}
        />
      </div>
      <div className="flex mt-16 gap-[48px]">
        <div className="flex flex-col">
          <label>{Lang.shippingState}</label>
          <Select
            className="mt-8"
            value={searchShippingState}
            options={ShippingStateSelects}
            onChange={selectShippingState}
          />
        </div>
        <div>
          <label>{Lang.shippingStateUpdatedDate}</label>
          <div className="flex items-center gap-[12px]">
            <input
              type="date"
              className="w-[160px]"
              value={from}
              onChange={(e) => changeFrom(e.target.value)}
            />
            <span className="text-3xl text-gray-300">〜</span>
            <input
              type="date"
              className="w-[160px]"
              value={till}
              onChange={(e) => changeTill(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <DefaultWithTitleLayout
      title={Lang.admin.vixellPallets.title}
      description={Lang.admin.vixellPallets.description}
      titleRightElement={titleRightElement}
      infiniteLoad={infiniteLoad}
      csvDownload={csvDownload}
    >
      {pallets && (
        <VixellTable
          rootClassName="my-64"
          classNames={['w-200', 'w-200', 'w-200', 'flex-1', 'w-140', 'w-200']}
          headers={[
            Lang.admin.vixellPallets.serialNo,
            Lang.model,
            Lang.siteName,
            Lang.admin.vixellPallets.composedVicSerial,
            Lang.shippingState,
            Lang.updatedDateTime,
          ]}
          elements={pallets.map((pallet) => [
            pallet.serialNo,
            pallet.model,
            pallet.siteName,
            <VixellPalletVixellList vixells={pallet.vixells} />,
            pallet.shippingState &&
              Lang.shippingStateName[pallet.shippingState],
            pallet.belongingLogProcessingDateTime && (
              <TableElementDatetime2Line
                dateTime={pallet.belongingLogProcessingDateTime}
              />
            ),
          ])}
          onLineClick={(index) =>
            navigate(`/vixell-pallets/${pallets[index].id}`)
          }
        />
      )}
      {showSearchModal && (
        <AdminVixellsSearchModal hide={() => setShowSearchModal(false)} />
      )}
    </DefaultWithTitleLayout>
  )
}
